<script setup lang="ts">
import type {
  AssessmentNavigationAction,
  AssessmentNavigationButtonSize,
  AssessmentNavigationButtonVariant,
  AssessmentNavigationIconPosition,
} from "#bb/types/blocks/assessment/AssessmentNavigationOption";
import { normalizeAssessmentNavigation } from "#bb/lib/assessmentNavigation";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { block, field, patch } = useInlineBlockEditor(props.id);
const configuration = computed(() => normalizeAssessmentNavigation(block.value));

const alignment = field("alignment", "between");
const size = field<AssessmentNavigationButtonSize>("size", "default");

const actionSections = computed(() => [
  {
    key: "back" as const,
    title: t("back.title"),
    icon: "lucide:arrow-left",
    action: configuration.value.back,
  },
  {
    key: "next" as const,
    title: t("next.title"),
    icon: "lucide:arrow-right",
    action: configuration.value.next,
  },
]);

const updateAction = (
  key: "back" | "next",
  actionPatch: Partial<AssessmentNavigationAction>,
) => {
  patch({
    [key]: {
      ...configuration.value[key],
      ...actionPatch,
    },
  });
};
</script>

<template>
  <EditorSection :title="t('appearance.title')" icon="lucide:panel-bottom" accent="assessment">
    <EditorField :label="t('appearance.alignment')">
      <Select
        :model-value="alignment"
        @update:model-value="(value) => (alignment = value as string)"
      >
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="between">{{ t("appearance.between") }}</SelectItem>
          <SelectItem value="start">{{ t("appearance.start") }}</SelectItem>
          <SelectItem value="center">{{ t("appearance.center") }}</SelectItem>
          <SelectItem value="end">{{ t("appearance.end") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('appearance.size')">
      <Select
        :model-value="size"
        @update:model-value="(value) => (size = value as AssessmentNavigationButtonSize)"
      >
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="sm">{{ t("appearance.small") }}</SelectItem>
          <SelectItem value="default">{{ t("appearance.medium") }}</SelectItem>
          <SelectItem value="lg">{{ t("appearance.large") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>
  </EditorSection>

  <EditorSection
    v-for="section in actionSections"
    :key="section.key"
    :title="section.title"
    :icon="section.icon"
    accent="assessment"
  >
    <div class="grid grid-cols-2 gap-2">
      <EditorField :label="t('action.visible')">
        <div class="flex h-8 items-center justify-between rounded-sm bg-muted px-2">
          <span class="text-[12.5px] text-muted-foreground">{{ t("action.enabled") }}</span>
          <Switch
            :model-value="section.action.visible"
            @update:model-value="(value) => updateAction(section.key, { visible: value })"
          />
        </div>
      </EditorField>

      <EditorField :label="t('action.interactive')">
        <div class="flex h-8 items-center justify-between rounded-sm bg-muted px-2">
          <span class="text-[12.5px] text-muted-foreground">{{ t("action.enabled") }}</span>
          <Switch
            :model-value="section.action.enabled"
            @update:model-value="(value) => updateAction(section.key, { enabled: value })"
          />
        </div>
      </EditorField>
    </div>

    <EditorField :label="t('action.label')">
      <Input
        :model-value="section.action.label"
        class="h-8 rounded-sm bg-muted text-[13px]"
        :placeholder="section.key === 'back' ? 'Back' : 'Next'"
        @update:model-value="(value) => updateAction(section.key, { label: String(value) })"
      />
    </EditorField>

    <div class="grid grid-cols-2 gap-2">
      <EditorField :label="t('action.icon')">
        <Input
          :model-value="section.action.icon"
          class="h-8 rounded-sm bg-muted font-mono text-[13px]"
          :placeholder="section.icon"
          @update:model-value="(value) => updateAction(section.key, { icon: String(value) })"
        />
      </EditorField>

      <EditorField :label="t('action.iconPosition')">
        <Select
          :model-value="section.action.iconPosition"
          @update:model-value="(value) => updateAction(section.key, {
            iconPosition: value as AssessmentNavigationIconPosition,
          })"
        >
          <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="left">{{ t("action.left") }}</SelectItem>
            <SelectItem value="right">{{ t("action.right") }}</SelectItem>
          </SelectContent>
        </Select>
      </EditorField>
    </div>

    <EditorField :label="t('action.variant')">
      <Select
        :model-value="section.action.variant"
        @update:model-value="(value) => updateAction(section.key, {
          variant: value as AssessmentNavigationButtonVariant,
        })"
      >
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default">Default</SelectItem>
          <SelectItem value="outline">Outline</SelectItem>
          <SelectItem value="secondary">Secondary</SelectItem>
          <SelectItem value="ghost">Ghost</SelectItem>
          <SelectItem value="link">Link</SelectItem>
          <SelectItem value="destructive">Destructive</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('action.validate')" :hint="t('action.validateHint')">
      <div class="flex h-8 items-center justify-between rounded-sm bg-muted px-2">
        <span class="text-[12.5px] text-muted-foreground">{{ t("action.enabled") }}</span>
        <Switch
          :model-value="section.action.validateForm"
          @update:model-value="(value) => updateAction(section.key, { validateForm: value })"
        />
      </div>
    </EditorField>

    <EditorField :label="t('action.automation')" :hint="t('action.automationHint')">
      <EditorPathInput
        :model-value="section.action.automationPath"
        mode="existing-file"
        :scopes="['site']"
        content-type-filter="automation"
        placeholder="/automations/assessment-page"
        aria-label="Automation path"
        @update:model-value="(value) => updateAction(section.key, {
          automationPath: String(value),
        })"
      />
    </EditorField>

    <EditorField :label="t('action.redirect')" :hint="t('action.redirectHint')">
      <EditorPathInput
        :model-value="section.action.navigationPath"
        mode="existing-file"
        :scopes="['site', 'external']"
        allow-http-urls
        placeholder="/assessment/page-2 or https://example.com"
        aria-label="Redirect path"
        @update:model-value="(value) => updateAction(section.key, {
          navigationPath: String(value),
        })"
      />
    </EditorField>

    <EditorField :label="t('action.preload')" :hint="t('action.preloadHint')">
      <div class="flex h-8 items-center justify-between rounded-sm bg-muted px-2">
        <span class="text-[12.5px] text-muted-foreground">{{ t("action.enabled") }}</span>
        <Switch
          :model-value="section.action.preloadDestination"
          @update:model-value="(value) => updateAction(section.key, {
            preloadDestination: value,
          })"
        />
      </div>
    </EditorField>

    <EditorField :label="t('action.delay')" :hint="t('action.delayHint')">
      <Input
        :model-value="section.action.navigationDelaySeconds"
        type="number"
        min="0"
        class="h-8 rounded-sm bg-muted text-[13px]"
        @update:model-value="(value) => updateAction(section.key, {
          navigationDelaySeconds: Number(value),
        })"
      />
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  appearance:
    title: Appearance
    alignment: Alignment
    between: Back left, Next right
    start: Start
    center: Center
    end: End
    size: Button size
    small: Small
    medium: Medium
    large: Large
  back:
    title: Back button
  next:
    title: Next button
  action:
    visible: Visible
    interactive: Interactive
    enabled: Enabled
    label: Button text
    icon: Icon
    iconPosition: Icon position
    left: Left
    right: Right
    variant: Variant
    validate: Validate form
    validateHint: Validate the nearest enclosing Form before running this action.
    automation: Automation path
    automationHint: Optional automation to run after validation succeeds.
    redirect: Redirect URL
    redirectHint: Optional site-local path or full HTTP(S) URL.
    preload: Preload destination
    preloadHint: Load an internal destination in the background so it is ready before the candidate clicks.
    delay: Redirect delay (seconds)
    delayHint: Wait after the automation finishes before navigating.
</i18n>
